import { useState } from 'react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@repo/ui/src/components/ui/card';
import { Button } from '@repo/ui/src/components/ui/button';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@repo/ui/src/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import { Banknote, ChevronDown, Clock, Lock, MailPlus, MoreVertical, Trash2, UserPlus, Users, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { inviteMemberSchema, createPlaceholderMemberSchema, convertMemberSchema } from '@repo/validation';
import { useAuthStore } from '../../../stores/authStore';
import { useCan } from '../../../hooks/useCan';
import { useSubscription } from '../../../hooks/useSubscription';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import {
  useMembers,
  useInvitations,
  useInviteMember,
  useRevokeInvitation,
  useUpdateMemberRole,
  useRemoveMember,
  useMemberRemovalPreview,
  useCreatePlaceholderMember,
  useConvertMember,
  type MemberPaySetting,
} from '../../../hooks/useTeam';
import { TeacherPayRateDialog } from '../../../components/TeacherPayRateDialog';
import type { MemberRole } from '@repo/shared-types';

const ROLE_STYLES: Record<string, string> = {
  OWNER: 'text-brand-700 border-brand-200 bg-brand-50',
  ADMIN: 'text-blue-700 border-blue-200 bg-blue-50',
  MEMBER: 'text-surface-600 border-surface-200 bg-surface-50',
};

export function TeamTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const { t: tp } = useTranslation('payroll');
  const currentUserId = useAuthStore((s) => s.user?.id);
  const currentRole = useAuthStore((s) => s.role);
  const canManage = useCan('manage:members');
  const isOwner = currentRole === 'OWNER';

  const { data: members = [], isLoading: membersLoading } = useMembers();
  const { data: invitations = [], isLoading: invitationsLoading } = useInvitations();
  const { data: subscription } = useSubscription();
  const inviteMember = useInviteMember();
  const revokeInvitation = useRevokeInvitation();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const removalPreview = useMemberRemovalPreview();
  const createPlaceholder = useCreatePlaceholderMember();
  const convertMember = useConvertMember();
  const openUpgradeDialog = useUpgradeDialogStore((s) => s.open);

  // Outstanding invitations = not yet accepted AND not expired — matches the
  // backend's seat-count definition exactly (assertWithinLimit's 'seat' branch
  // in tierLimits.ts counts memberships + invitations meeting this same
  // condition). Distinct from `pendingInvitations` below, which the "Pending
  // Invitations" list renders and intentionally includes expired-but-unaccepted
  // invites too (so the owner can see and re-invite them).
  const outstandingInvitationsCount = invitations.filter(
    (inv) => !inv.acceptedAt && new Date(inv.expiresAt) > new Date(),
  ).length;

  // Teacher seat cap: -1 means unlimited (the generic sentinel; no current
  // plan actually uses it — Center's cap is 20 seats as of THI-149). The
  // backend counts active memberships + outstanding invitations (see above) —
  // mirror that here so the displayed "used/max" and the at-cap affordance
  // don't under-report usage relative to what a POST /invite or
  // /members/placeholder will actually enforce.
  const maxSeats = subscription?.plan.maxTeacherSeats ?? -1;
  const seatsUsed = members.length + outstandingInvitationsCount;
  const atSeatCap = maxSeats !== -1 && seatsUsed >= maxSeats;

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [inviteError, setInviteError] = useState('');

  const [showQuickAddDialog, setShowQuickAddDialog] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddError, setQuickAddError] = useState('');

  const [convertTargetId, setConvertTargetId] = useState<string | null>(null);
  const [convertEmail, setConvertEmail] = useState('');
  const [convertError, setConvertError] = useState('');

  const [removeTarget, setRemoveTarget] = useState<{
    id: string;
    name: string;
    activeSlots: number;
    futureSessions: number;
  } | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null);

  // Teacher-payroll rate dialog. Prefilled straight from the (already-fetched)
  // members list — GET /org/members joins TeacherPaySetting in, so there's no
  // second round trip on open. Rendering the dropdown item is itself gated on
  // the plan flag; opening the dialog is a secondary defense in depth.
  const [paySettingTarget, setPaySettingTarget] = useState<{
    id: string;
    name: string;
    paySetting: MemberPaySetting | null;
  } | null>(null);

  function handleOpenPaySetting(member: {
    id: string;
    user: { fullName: string | null };
    paySetting?: MemberPaySetting | null;
  }) {
    setPaySettingTarget({
      id: member.id,
      name: member.user.fullName || '—',
      paySetting: member.paySetting ?? null,
    });
  }

  function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError('');

    const result = inviteMemberSchema.safeParse({ email: inviteEmail, role: inviteRole });
    if (!result.success) {
      setInviteError(result.error.issues[0]?.message ?? 'Validation error');
      return;
    }

    inviteMember.mutate(result.data, {
      onSuccess: () => {
        setShowInviteDialog(false);
        setInviteEmail('');
        setInviteRole('MEMBER');
      },
    });
  }

  function handleQuickAdd(e: React.FormEvent) {
    e.preventDefault();
    setQuickAddError('');

    const result = createPlaceholderMemberSchema.safeParse({ name: quickAddName });
    if (!result.success) {
      setQuickAddError(result.error.issues[0]?.message ?? 'Validation error');
      return;
    }

    createPlaceholder.mutate(result.data, {
      onSuccess: () => {
        setShowQuickAddDialog(false);
        setQuickAddName('');
      },
    });
  }

  function handleConvert(e: React.FormEvent) {
    e.preventDefault();
    setConvertError('');
    if (!convertTargetId) return;

    const result = convertMemberSchema.safeParse({ email: convertEmail });
    if (!result.success) {
      setConvertError(result.error.issues[0]?.message ?? 'Validation error');
      return;
    }

    convertMember.mutate(
      { id: convertTargetId, ...result.data },
      {
        onSuccess: () => {
          setConvertTargetId(null);
          setConvertEmail('');
        },
      },
    );
  }

  function handleRoleChange(membershipId: string, role: MemberRole) {
    if (role === 'OWNER') return;
    updateRole.mutate({ id: membershipId, role: role as 'ADMIN' | 'MEMBER' });
  }

  // Fetch the impact-preview counts before opening the confirm dialog, so the
  // owner sees exactly how many classes/sessions removal will affect.
  function handleRemoveClick(membershipId: string, name: string) {
    removalPreview.mutate(membershipId, {
      onSuccess: (data) => {
        setRemoveTarget({ id: membershipId, name, ...data });
      },
      onError: (err: Error) => {
        toast.error(err.message || t('team.removePreviewError'));
      },
    });
  }

  function handleConfirmRemove() {
    if (!removeTarget) return;
    removeMember.mutate(
      { id: removeTarget.id, name: removeTarget.name },
      { onSuccess: () => setRemoveTarget(null) },
    );
  }

  function handleConfirmRevoke() {
    if (!revokeTarget) return;
    revokeInvitation.mutate(revokeTarget.id, { onSuccess: () => setRevokeTarget(null) });
  }

  // Pending (non-accepted) invitations — rendered below, including expired
  // ones (so the owner can see and re-invite them).
  const pendingInvitations = invitations.filter((inv) => !inv.acceptedAt);

  return (
    <div className="space-y-6">
      {/* Members */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">{t('team.title')}</CardTitle>
            <CardDescription className="mt-1">
              {t('team.memberCount', { count: members.length })}
              {subscription && (
                <span className="ml-2 text-surface-400">
                  · {t('team.seatCount', { used: seatsUsed, max: maxSeats === -1 ? '∞' : maxSeats })}
                </span>
              )}
            </CardDescription>
          </div>
          {canManage &&
            (atSeatCap ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs font-semibold"
                onClick={() => openUpgradeDialog('seat')}
              >
                <Lock className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                {t('team.addTeacher')}
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="h-8 text-xs font-semibold">
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                    {t('team.addTeacher')}
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowInviteDialog(true)}>
                    <MailPlus className="w-3.5 h-3.5 mr-2" strokeWidth={1.8} />
                    {t('team.addTeacherEmailMode')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowQuickAddDialog(true)}>
                    <UserCog className="w-3.5 h-3.5 mr-2" strokeWidth={1.8} />
                    {t('team.addTeacherQuickMode')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-surface-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-surface-300 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-surface-500">{t('team.empty')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map((member) => {
                const isSelf = member.user.id === currentUserId;
                const isMemberOwner = member.role === 'OWNER';
                const canChangeRole = isOwner && !isSelf && !isMemberOwner;
                const canRemove = canManage && !isSelf && !isMemberOwner;

                const initials = member.user.fullName
                  ? member.user.fullName
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()
                  : '??';

                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-surface-200 hover:bg-surface-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-gradient-to-br from-brand-400 to-emerald-500 flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-surface-900 truncate">
                          {member.user.fullName || '—'}
                          {isSelf && (
                            <span className="ml-1.5 text-xs text-surface-400 font-normal">
                              {t('team.you')}
                            </span>
                          )}
                        </p>
                        {member.user.isPlaceholder ? (
                          <Badge variant="outline" className="mt-0.5 text-amber-700 border-amber-200 bg-amber-50">
                            {t('team.noAccountBadge')}
                          </Badge>
                        ) : (
                          <p className="text-xs text-surface-400 truncate">{member.user.email}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {member.user.isPlaceholder && canManage && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs font-medium"
                          onClick={() => {
                            setConvertTargetId(member.id);
                            setConvertEmail('');
                            setConvertError('');
                          }}
                        >
                          <UserCog className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
                          {t('team.convertAction')}
                        </Button>
                      )}
                      {canChangeRole ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-md hover:bg-surface-50 transition-colors">
                              <Badge variant="outline" className={ROLE_STYLES[member.role]}>
                                {tc(`roles.${member.role}`)}
                              </Badge>
                              <ChevronDown className="w-3 h-3 text-surface-400" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleRoleChange(member.id, 'ADMIN')}
                              className={member.role === 'ADMIN' ? 'font-semibold' : ''}
                            >
                              {tc('roles.ADMIN')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRoleChange(member.id, 'MEMBER')}
                              className={member.role === 'MEMBER' ? 'font-semibold' : ''}
                            >
                              {tc('roles.MEMBER')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Badge variant="outline" className={ROLE_STYLES[member.role]}>
                          {tc(`roles.${member.role}`)}
                        </Badge>
                      )}

                      {canManage && subscription?.plan.allowsPayroll && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-surface-400 hover:text-surface-700"
                            >
                              <MoreVertical className="w-3.5 h-3.5" strokeWidth={1.8} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenPaySetting(member)}>
                              <Banknote className="w-3.5 h-3.5 mr-2" strokeWidth={1.8} />
                              {tp('settings.menuItem')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                      {canRemove && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-surface-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() =>
                            handleRemoveClick(member.id, member.user.fullName || '—')
                          }
                          disabled={
                            (removalPreview.isPending && removalPreview.variables === member.id) ||
                            removeMember.isPending
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('team.pendingTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {invitationsLoading ? (
              <div className="h-12 bg-surface-100 animate-pulse rounded-lg" />
            ) : pendingInvitations.length === 0 ? (
              <div className="text-center py-6">
                <MailPlus className="w-8 h-8 text-surface-300 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-xs text-surface-400">{t('team.pendingEmpty')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingInvitations.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-dashed border-surface-200"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-surface-700 truncate">{inv.email}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={ROLE_STYLES[inv.role]}>
                          {tc(`roles.${inv.role}`)}
                        </Badge>
                        <span className="flex items-center gap-1 text-xs text-surface-400">
                          <Clock className="w-3 h-3" strokeWidth={1.8} />
                          {t('team.expires', {
                            date: new Date(inv.expiresAt).toLocaleDateString('vi-VN', {
                              day: '2-digit',
                              month: '2-digit',
                            }),
                          })}
                        </span>
                      </div>
                    </div>
                    {/* THI-175: revoke frees the seat this invitation is
                        still holding — no more waiting out the 7-day expiry. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-surface-400 hover:text-destructive"
                      onClick={() => setRevokeTarget({ id: inv.id, email: inv.email })}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
                      {t('team.revoke')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('team.inviteTitle')}</DialogTitle>
            <DialogDescription>
              {t('team.inviteDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="invite-email" className="text-sm font-medium text-surface-700">
                {t('team.inviteEmail')}
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('team.inviteEmailPlaceholder')}
                className="mt-1.5"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-surface-700">{t('team.inviteRole')}</Label>
              <div className="mt-1.5 flex gap-2">
                <button
                  type="button"
                  onClick={() => setInviteRole('MEMBER')}
                  className={`flex-1 px-3 py-2 text-sm font-medium border rounded-md transition-colors ${
                    inviteRole === 'MEMBER'
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                  }`}
                >
                  {tc('roles.MEMBER')}
                </button>
                <button
                  type="button"
                  onClick={() => setInviteRole('ADMIN')}
                  className={`flex-1 px-3 py-2 text-sm font-medium border rounded-md transition-colors ${
                    inviteRole === 'ADMIN'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                  }`}
                >
                  {tc('roles.ADMIN')}
                </button>
              </div>
            </div>

            {(inviteError || inviteMember.error) && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
                <p className="text-sm text-red-700">
                  {inviteError || (inviteMember.error as Error).message}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowInviteDialog(false)}
              >
                {t('team.inviteCancel')}
              </Button>
              <Button type="submit" disabled={inviteMember.isPending} className="font-semibold">
                {inviteMember.isPending ? t('team.inviteSubmitting') : t('team.inviteSubmit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick-add (accountless placeholder teacher) Dialog */}
      <Dialog open={showQuickAddDialog} onOpenChange={setShowQuickAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('team.quickAddTitle')}</DialogTitle>
            <DialogDescription>{t('team.quickAddDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickAdd} className="space-y-4">
            <div>
              <Label htmlFor="quick-add-name" className="text-sm font-medium text-surface-700">
                {t('team.quickAddName')}
              </Label>
              <Input
                id="quick-add-name"
                type="text"
                value={quickAddName}
                onChange={(e) => setQuickAddName(e.target.value)}
                placeholder={t('team.quickAddNamePlaceholder')}
                className="mt-1.5"
                autoFocus
              />
            </div>

            {(quickAddError || createPlaceholder.error) && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
                <p className="text-sm text-red-700">
                  {quickAddError || (createPlaceholder.error as Error).message}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuickAddDialog(false)}>
                {t('team.inviteCancel')}
              </Button>
              <Button type="submit" disabled={createPlaceholder.isPending} className="font-semibold">
                {createPlaceholder.isPending ? t('team.inviteSubmitting') : t('team.quickAddSubmit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Teacher-payroll rate dialog — shared with the payroll table's rate
          column (TeacherPayRateDialog); do not fork a second copy here. */}
      <TeacherPayRateDialog
        membershipId={paySettingTarget?.id ?? ''}
        teacherName={paySettingTarget?.name ?? ''}
        initial={
          paySettingTarget
            ? {
                rules: paySettingTarget.paySetting?.payRules ?? [],
                rateUnit: paySettingTarget.paySetting?.rateUnit ?? 'PER_SESSION',
                baseSalary: paySettingTarget.paySetting?.baseSalary ?? null,
              }
            : null
        }
        open={paySettingTarget !== null}
        onOpenChange={(open) => !open && setPaySettingTarget(null)}
      />

      {/* Convert-to-account Dialog */}
      <Dialog open={convertTargetId !== null} onOpenChange={(open) => !open && setConvertTargetId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('team.convertTitle')}</DialogTitle>
            <DialogDescription>{t('team.convertDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConvert} className="space-y-4">
            <div>
              <Label htmlFor="convert-email" className="text-sm font-medium text-surface-700">
                {t('team.convertEmail')}
              </Label>
              <Input
                id="convert-email"
                type="email"
                value={convertEmail}
                onChange={(e) => setConvertEmail(e.target.value)}
                placeholder={t('team.inviteEmailPlaceholder')}
                className="mt-1.5"
                autoFocus
              />
            </div>

            {(convertError || convertMember.error) && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
                <p className="text-sm text-red-700">
                  {convertError || (convertMember.error as Error).message}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConvertTargetId(null)}>
                {t('team.inviteCancel')}
              </Button>
              <Button type="submit" disabled={convertMember.isPending} className="font-semibold">
                {convertMember.isPending ? t('team.inviteSubmitting') : t('team.convertSubmit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove-teacher confirmation — shows the removal-preview impact
          counts (active slots + future sessions) before committing. */}
      <AlertDialog open={removeTarget !== null} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('team.removeConfirmTitle', { name: removeTarget?.name ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('team.removeConfirmDescription', {
                activeSlots: removeTarget?.activeSlots ?? 0,
                futureSessions: removeTarget?.futureSessions ?? 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('team.inviteCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={removeMember.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {removeMember.isPending ? t('team.inviteSubmitting') : t('team.removeConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke-invitation confirmation (THI-175) — a plain confirm, no
          impact preview needed: revoking only frees a seat, nothing else. */}
      <AlertDialog open={revokeTarget !== null} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('team.revokeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('team.revokeConfirmDescription', { email: revokeTarget?.email ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('team.inviteCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRevoke}
              disabled={revokeInvitation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {revokeInvitation.isPending ? t('team.inviteSubmitting') : t('team.revokeConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
